import { useState } from 'react';
import type { FC } from 'react';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Button } from '@launchpad-ui/components';

import type { FlagEnvSummary } from './deriveFlagEnvSummaries';

import * as styles from './FlagEnvironmentList.css';

export interface FlagEnvironmentListProps {
  environments: readonly FlagEnvSummary[];
  density: 'card' | 'detail';
  initialVisibleCount?: number;
}

const environmentPercent = (environment: FlagEnvSummary): number => environment.percent ?? (environment.on ? 100 : 0);

/**
 * Shared environment rollout list for artifact cards and the detail panel.
 *
 * Cards sit inside a navigation link, so they cannot host a disclosure button —
 * overflow is shown as inert "+N more". The detail panel can expand in place.
 */
export const FlagEnvironmentList: FC<FlagEnvironmentListProps> = ({ environments, density, initialVisibleCount }) => {
  const visibleCount = initialVisibleCount !== undefined ? initialVisibleCount : density === 'card' ? 2 : 3;
  const [expanded, setExpanded] = useState(false);
  const canDisclose = density === 'detail' && environments.length > visibleCount;
  const visibleEnvironments =
    density === 'card' || (canDisclose && !expanded) ? environments.slice(0, visibleCount) : environments;
  const hiddenCount = environments.length - visibleEnvironments.length;

  return (
    <>
      <ul className={styles.list}>
        {visibleEnvironments.map((environment) => {
          const percent = environmentPercent(environment);
          const compact = `${environment.name} · ${percent}% · ${environment.health ?? environment.method}`;

          return (
            <li key={environment.key} className={styles.row[density]}>
              <span className={styles.environmentName}>{environment.name}</span>
              <span className={styles.progressTrack} aria-hidden>
                <span className={styles.progressFill} style={{ width: `${Math.max(0, Math.min(100, percent))}%` }} />
              </span>
              <span className={styles.environmentMeta}>
                <span className={styles.metaWide}>
                  <span className={styles.percent}>{percent}%</span>
                  <span>{environment.method}</span>
                  {environment.health ? <span>· {environment.health}</span> : null}
                </span>
                <span className={styles.metaCompact}>{compact}</span>
                {environment.nextStep ? <span className={styles.nextStep}>{environment.nextStep}</span> : null}
              </span>
            </li>
          );
        })}
      </ul>
      {density === 'card' && hiddenCount > 0 ? (
        <p
          className={styles.moreEnvironments}
          aria-label={msg('vega.artifactCard.moreEnvironmentsLabel', { count: hiddenCount })}
        >
          <Message id="vega.artifactCard.moreEnvironments" values={{ count: hiddenCount }} />
        </p>
      ) : null}
      {canDisclose ? (
        <div className={styles.disclosure}>
          <Button variant="minimal" size="small" onPress={() => setExpanded((current) => !current)}>
            {expanded
              ? msg('vega.artifactCard.showFewerEnvironments')
              : msg('vega.artifactCard.showMoreEnvironments', { count: hiddenCount })}
          </Button>
        </div>
      ) : null}
    </>
  );
};
